import { Field, Form, Formik } from "formik";

import { CaseAdmin, Clinic, User } from "pwapi";

import { CaseAdminDetail } from "shared/reducers/admin/adminCaseReducer";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import TextInput from "components/customInputs/TextInput";

import FormikContext from "./FormikContext";
import { AdminFieldClinic, AdminFieldOrderingDoctor } from "../edit/fields";

type IntegrationsFormProps = {
  initialFields: {
    clinic?: Clinic;
    external_ezderm_order_id?: string | null;
    external_order_id?: string | null;
    external_patient_id?: string | null;
    orderingDoctor?: Partial<User>;
  };
  adminCase: CaseAdminDetail;
  patientId?: number;
  onSubmit: Function;
  setValues: Function;
  setEdited: Function;
  canUpdateCase: boolean;
};

const IntegrationsForm = ({
  initialFields,
  adminCase,
  patientId,
  onSubmit,
  setValues,
  setEdited,
  canUpdateCase,
}: IntegrationsFormProps) => {
  const initialValues = {
    clinic: initialFields.clinic,
    external_ezderm_order_id: initialFields.external_ezderm_order_id || "",
    external_order_id: initialFields.external_order_id || "",
    external_patient_id: initialFields.external_patient_id || "",
    orderingDoctor: initialFields.orderingDoctor,
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={(values) => {
        onSubmit({
          patientId,
          caseId: adminCase.id,
          clinicId: values?.clinic?.id,
          external_patient_id:
            ((values?.clinic?.ezderm_enabled ||
              values?.clinic?.modmed_clinic_id ||
              values?.clinic?.ecw_clinic_id ||
              values?.clinic?.nextech_clinic_id) &&
              values.external_patient_id) ||
            null,
          external_order_id:
            ((values?.clinic?.modmed_clinic_id ||
              values?.clinic?.nextech_clinic_id ||
              values?.clinic?.ecw_clinic_id) &&
              values.external_order_id) ||
            null,
          external_ezderm_order_id:
            (values?.clinic?.ezderm_enabled &&
              values.external_ezderm_order_id) ||
            null,
          orderingDoctorId: values?.orderingDoctor?.id,
        });
      }}
    >
      {({ resetForm, dirty, values }) => (
        <Form className="AdminPanelForm">
          <FormikContext setValues={setValues} setEdited={setEdited} />
          <div className="panelContainer">
            <div
              className="panelContainer__inputGroup"
              style={{ width: "100%" }}
            >
              <fieldset className="panelContainer__section" style={{ flex: 1 }}>
                <legend className="panelContainer__section--title">
                  Patient Clinic
                </legend>
                <Field name="clinic">
                  {({ field, form }: any) => (
                    <AdminFieldClinic
                      {...field}
                      onChange={(newValue: any) => {
                        form.setFieldValue("clinic", newValue.object);
                        form.setFieldValue("orderingDoctor", undefined);
                      }}
                      disabled={!canUpdateCase}
                    />
                  )}
                </Field>
              </fieldset>
              <fieldset className="panelContainer__section" style={{ flex: 1 }}>
                <legend className="panelContainer__section--title">
                  Ordering Provider
                </legend>
                <Field name="orderingDoctor">
                  {({ field, form }: any) => (
                    <AdminFieldOrderingDoctor
                      {...field}
                      value={field.value}
                      patientClinicId={values?.clinic?.id}
                      onChange={(newValue: any) => {
                        form.setFieldValue("orderingDoctor", newValue.object);
                        setEdited(true);
                      }}
                      disabled={!canUpdateCase || !values?.clinic}
                    />
                  )}
                </Field>
              </fieldset>
            </div>
            {adminCase.source === CaseAdmin.source.PW && (
              <fieldset className="panelContainer__section">
                <legend className="panelContainer__section--title">
                  External IDs
                </legend>
                <div className="panelContainer__inputGroup">
                  {values.clinic?.ezderm_enabled && (
                    <Field name="external_ezderm_order_id">
                      {({ field }: any) => (
                        <TextInput
                          label="EZDerm Order Number *"
                          id="external_ezderm_order_id"
                          {...field}
                          required
                          disabled={!canUpdateCase}
                        />
                      )}
                    </Field>
                  )}
                  {(values.clinic?.modmed_clinic_id ||
                    values.clinic?.ecw_clinic_id ||
                    values.clinic?.nextech_clinic_id) && (
                    <Field name="external_order_id">
                      {({ field }: any) => (
                        <TextInput
                          label={
                            values.clinic?.ecw_clinic_id
                              ? "Req/Ctrl# *"
                              : `${
                                  values.clinic?.modmed_clinic_id
                                    ? "ModMed "
                                    : values.clinic?.nextech_clinic_id &&
                                      "NexTech "
                                }Order Number *`
                          }
                          id="external_order_id"
                          {...field}
                          required
                          disabled={!canUpdateCase}
                        />
                      )}
                    </Field>
                  )}
                  {(values.clinic?.modmed_clinic_id ||
                    values.clinic?.ezderm_enabled ||
                    values.clinic?.nextech_clinic_id ||
                    values.clinic?.ecw_clinic_id) && (
                    <Field name="external_patient_id">
                      {({ field }: any) => (
                        <TextInput
                          label={
                            values.clinic?.modmed_clinic_id
                              ? "EMA ID *"
                              : values.clinic?.ecw_clinic_id ||
                                values.clinic?.nextech_clinic_id
                              ? "Encounter ID *"
                              : "External MRN"
                          }
                          id="external_patient_id"
                          {...field}
                          required={
                            !!values.clinic?.modmed_clinic_id ||
                            !!values.clinic?.nextech_clinic_id ||
                            !!values.clinic?.ecw_clinic_id
                          }
                          disabled={!canUpdateCase}
                        />
                      )}
                    </Field>
                  )}
                  {!values.clinic?.modmed_clinic_id &&
                    !values.clinic?.ezderm_enabled &&
                    !values.clinic?.nextech_clinic_id &&
                    !values.clinic?.ecw_clinic_id && (
                      <span>Clinic has no integrations</span>
                    )}
                </div>
              </fieldset>
            )}

            <div className="form__actionContainer">
              <PathologyWatchButton
                title="Discard changes to case"
                color="red"
                onClick={() => {
                  resetForm();
                }}
                disabled={!dirty}
              >
                Discard Changes
              </PathologyWatchButton>
              <PathologyWatchButton
                type="submit"
                title="Save case"
                disabled={!dirty || !canUpdateCase}
                color="green"
              >
                Save Changes
              </PathologyWatchButton>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default IntegrationsForm;
